/**
 * EditGroupDialog Component
 * Dialog for editing local group details (name, description, tags)
 *
 * Migrated to BaseDialog pattern (Phase 3.2)
 */

import React, { useState, useEffect } from 'react';
import {
  Button,
  TextField,
  Box,
  Chip,
  IconButton,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { BaseDialog } from '../ui/BaseDialog';
import { useUpdateGroup } from '../../hooks/api/useGroups';
import type { AppGroup } from '../../types/group';

interface EditGroupDialogProps {
  group: AppGroup;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const EditGroupDialog: React.FC<EditGroupDialogProps> = ({
  group,
  open,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const updateGroupMutation = useUpdateGroup();

  // Form state
  const [formData, setFormData] = useState({
    displayName: '',
    description: '',
  });
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [errors, setErrors] = useState<{
    displayName?: string;
    description?: string;
  }>({});

  // Initialize form data when dialog opens
  useEffect(() => {
    if (open) {
      setFormData({
        displayName: group.displayName || group.name || '',
        description: group.description || '',
      });
      setTags(group.tags || []);
      setErrors({});
      setTagInput('');
      updateGroupMutation.reset();
    }
  }, [open, group]);

  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};

    // Validate displayName
    if (!formData.displayName.trim()) {
      newErrors.displayName = t('groups.edit.nameRequired');
    } else if (formData.displayName.trim().length < 3) {
      newErrors.displayName = t('groups.edit.nameTooShort');
    } else if (formData.displayName.length > 100) {
      newErrors.displayName = t('groups.edit.nameTooLong');
    }

    // Validate description
    if (formData.description.length > 500) {
      newErrors.description = t('groups.edit.descriptionTooLong');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddTag = () => {
    const trimmedTag = tagInput.trim();
    if (trimmedTag && !tags.includes(trimmedTag)) {
      setTags([...tags, trimmedTag]);
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter((tag) => tag !== tagToRemove));
  };

  const handleTagInputKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleAddTag();
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    await updateGroupMutation.mutateAsync({
      groupId: group.id,
      data: {
        display_name: formData.displayName.trim(),
        description: formData.description.trim(),
        tags: tags,
      },
    });

    onSuccess();
    onClose();
  };

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      title={t('groups.edit.title')}
      maxWidth="sm"
      fullWidth
      loading={updateGroupMutation.isPending}
      error={
        updateGroupMutation.error instanceof Error
          ? updateGroupMutation.error.message
          : updateGroupMutation.error
          ? t('groups.edit.error')
          : null
      }
      actions={
        <>
          <Button onClick={onClose}>
            {t('groups.edit.cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
          >
            {t('groups.edit.save')}
          </Button>
        </>
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Group Name */}
        <TextField
          label={t('groups.edit.name')}
          placeholder={t('groups.edit.namePlaceholder')}
          helperText={errors.displayName || t('groups.edit.nameHelper')}
          value={formData.displayName}
          onChange={(e) =>
            setFormData({ ...formData, displayName: e.target.value })
          }
          error={Boolean(errors.displayName)}
          required
          fullWidth
        />

        {/* Description */}
        <TextField
          label={t('groups.edit.description')}
          placeholder={t('groups.edit.descriptionPlaceholder')}
          helperText={errors.description || t('groups.edit.descriptionHelper')}
          value={formData.description}
          onChange={(e) =>
            setFormData({ ...formData, description: e.target.value })
          }
          error={Boolean(errors.description)}
          multiline
          rows={3}
          fullWidth
        />

        {/* Tags */}
        <Box>
          <TextField
            label={t('groups.edit.tags')}
            placeholder={t('groups.edit.tagsPlaceholder')}
            helperText={t('groups.edit.tagsHelper')}
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyPress={handleTagInputKeyPress}
            fullWidth
            slotProps={{
              input: {
                endAdornment: (
                  <IconButton
                    onClick={handleAddTag}
                    disabled={!tagInput.trim()}
                    size="small"
                  >
                    <AddIcon />
                  </IconButton>
                ),
              },
            }}
          />

          {tags.length > 0 && (
            <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {tags.map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  onDelete={() => handleRemoveTag(tag)}
                  size="small"
                />
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </BaseDialog>
  );
};
